package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.ColorMath
import co.maytes.standupbot.shared.design.DesignTokens
import co.maytes.standupbot.shared.design.Rgba
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.design.StatusRole
import co.maytes.standupbot.shared.design.ThemeScheme
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Div

private const val TEXT_MINIMUM = 4.5
private const val UI_MINIMUM = 3.0

private class PairingCheck(val label: String, val backdropName: String, val minimum: Double?)

private class SwatchSpec(
    val cssName: String,
    val label: String,
    val previewOn: String? = null,
    val checks: List<PairingCheck> = emptyList(),
)

private class SwatchGroup(val title: String, val note: String, val swatches: List<SwatchSpec>)

private fun textChecks(vararg backdrops: Pair<String, String>) =
    backdrops.map { (label, name) -> PairingCheck(label, name, TEXT_MINIMUM) }

private val allSurfaces = textChecks(
    "bg" to DesignTokens.BG,
    "surface" to DesignTokens.SURFACE,
    "surface-subtle" to DesignTokens.SURFACE_SUBTLE,
    "surface-hover" to DesignTokens.SURFACE_HOVER,
)

private fun statusSwatches(hue: StatusHue): List<SwatchSpec> {
    fun name(role: StatusRole) = DesignTokens.statusName(hue, role)
    return listOf(
        SwatchSpec(
            cssName = name(StatusRole.Solid),
            label = "${hue.cssName}/solid",
            checks = textChecks("surface" to DesignTokens.SURFACE, "surface-subtle" to DesignTokens.SURFACE_SUBTLE),
        ),
        SwatchSpec(
            cssName = name(StatusRole.SolidHover),
            label = "${hue.cssName}/solid-hover",
        ),
        SwatchSpec(
            cssName = name(StatusRole.OnSolid),
            label = "${hue.cssName}/on-solid",
            previewOn = name(StatusRole.Solid),
            checks = textChecks("solid" to name(StatusRole.Solid), "solid-hover" to name(StatusRole.SolidHover)),
        ),
        SwatchSpec(
            cssName = name(StatusRole.Subtle),
            label = "${hue.cssName}/subtle",
        ),
        SwatchSpec(
            cssName = name(StatusRole.OnSubtle),
            label = "${hue.cssName}/on-subtle",
            previewOn = name(StatusRole.Subtle),
            checks = textChecks("subtle" to name(StatusRole.Subtle)),
        ),
        SwatchSpec(
            cssName = name(StatusRole.Border),
            label = "${hue.cssName}/border",
            checks = listOf(PairingCheck("surface", DesignTokens.SURFACE, null)),
        ),
    )
}

private val swatchGroups: List<SwatchGroup> = listOf(
    SwatchGroup(
        "Surfaces and borders",
        "Layered neutrals. Borders are decorative hairlines: their ratio is shown for reference only.",
        listOf(
            SwatchSpec(DesignTokens.BG, "bg"),
            SwatchSpec(DesignTokens.SURFACE, "surface"),
            SwatchSpec(DesignTokens.SURFACE_SUBTLE, "surface-subtle"),
            SwatchSpec(DesignTokens.SURFACE_HOVER, "surface-hover"),
            SwatchSpec(DesignTokens.BORDER, "border", checks = listOf(PairingCheck("surface", DesignTokens.SURFACE, null))),
            SwatchSpec(
                DesignTokens.BORDER_SUBTLE,
                "border-subtle",
                checks = listOf(PairingCheck("surface", DesignTokens.SURFACE, null)),
            ),
        ),
    ),
    SwatchGroup(
        "Text",
        "Every text role must reach 4.5:1 on every surface it appears on.",
        listOf(
            SwatchSpec(DesignTokens.TEXT_PRIMARY, "text-primary", previewOn = DesignTokens.SURFACE, checks = allSurfaces),
            SwatchSpec(DesignTokens.TEXT_SECONDARY, "text-secondary", previewOn = DesignTokens.SURFACE, checks = allSurfaces),
            SwatchSpec(DesignTokens.TEXT_TERTIARY, "text-tertiary", previewOn = DesignTokens.SURFACE, checks = allSurfaces),
        ),
    ),
    SwatchGroup(
        "Primary",
        "The near-black call-to-action fill and the text that sits on it.",
        listOf(
            SwatchSpec(DesignTokens.PRIMARY, "primary"),
            SwatchSpec(DesignTokens.PRIMARY_HOVER, "primary-hover"),
            SwatchSpec(
                DesignTokens.ON_PRIMARY,
                "on-primary",
                previewOn = DesignTokens.PRIMARY,
                checks = textChecks("primary" to DesignTokens.PRIMARY, "primary-hover" to DesignTokens.PRIMARY_HOVER),
            ),
        ),
    ),
    SwatchGroup(
        "Accent",
        "accent is for fills and the focus ring (3:1); accent-text is for small accent text on any surface.",
        listOf(
            SwatchSpec(
                DesignTokens.ACCENT,
                "accent",
                checks = listOf(
                    PairingCheck("surface", DesignTokens.SURFACE, TEXT_MINIMUM),
                    PairingCheck("surface-subtle", DesignTokens.SURFACE_SUBTLE, UI_MINIMUM),
                ),
            ),
            SwatchSpec(DesignTokens.ACCENT_HOVER, "accent-hover"),
            SwatchSpec(DesignTokens.ACCENT_SUBTLE, "accent-subtle"),
            SwatchSpec(DesignTokens.ACCENT_BORDER, "accent-border"),
            SwatchSpec(
                DesignTokens.ACCENT_TEXT,
                "accent-text",
                previewOn = DesignTokens.SURFACE,
                checks = textChecks(
                    "surface" to DesignTokens.SURFACE,
                    "surface-subtle" to DesignTokens.SURFACE_SUBTLE,
                    "accent-subtle" to DesignTokens.ACCENT_SUBTLE,
                ),
            ),
            SwatchSpec(
                DesignTokens.ON_ACCENT,
                "on-accent",
                previewOn = DesignTokens.ACCENT,
                checks = textChecks("accent" to DesignTokens.ACCENT, "accent-hover" to DesignTokens.ACCENT_HOVER),
            ),
        ),
    ),
) + StatusHue.entries.map { hue ->
    SwatchGroup(
        "Status: ${hue.cssName}",
        "solid for fills and icons, subtle plus on-subtle for badges and banners, border for rings.",
        statusSwatches(hue),
    )
}

private fun backdropFor(name: String, scheme: ThemeScheme): Rgba = DesignTokens.surface(name, scheme)

private fun displayValue(cssName: String, scheme: ThemeScheme): String {
    val color = ColorMath.parse(DesignTokens.color(cssName).value(scheme))
    return if (color.isOpaque) ColorMath.toHex(color) else DesignTokens.color(cssName).value(scheme)
}

@Composable
fun ColourSection(scheme: ThemeScheme) {
    GallerySection(
        id = "colour",
        title = "Colour",
        description = "Swatches show the value in the current theme and the measured WCAG contrast against the " +
            "background each role is used on. Ratios come from the same function the unit test runs.",
    ) {
        swatchGroups.forEach { group ->
            Stack(10) {
                GalleryHeading(group.title)
                GalleryNote(group.note)
                GalleryGrid(minColumn = 236) {
                    group.swatches.forEach { Swatch(it, scheme) }
                }
            }
        }
    }
}

@Composable
private fun Swatch(spec: SwatchSpec, scheme: ThemeScheme) {
    GalleryPanel {
        Div(
            attrs = {
                style {
                    property("height", "56px")
                    property("border-radius", "8px")
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("display", "flex")
                    property("align-items", "center")
                    property("padding", "0 12px")
                    if (spec.previewOn == null) {
                        property("background-color", "var(--${spec.cssName})")
                    } else {
                        property("background-color", "var(--${spec.previewOn})")
                        property("color", "var(--${spec.cssName})")
                        property("font-size", "${Typography.h4.size}")
                        property("font-weight", "600")
                    }
                }
            },
        ) {
            if (spec.previewOn != null) SpanText("Aa  Standup digest")
        }
        Stack(2) {
            GalleryHeading(spec.label)
            GalleryMono(displayValue(spec.cssName, scheme))
            GalleryMono("--${spec.cssName}", Palette.textTertiary)
        }
        spec.checks.forEach { check -> PairingRow(spec, check, scheme) }
    }
}

@Composable
private fun PairingRow(spec: SwatchSpec, check: PairingCheck, scheme: ThemeScheme) {
    val backdrop = backdropFor(check.backdropName, scheme)
    val foreground = DesignTokens.resolve(spec.cssName, scheme)
    val ratio = ColorMath.contrastRatio(foreground, backdrop)
    val text = ColorMath.formatRatio(ratio)
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("gap", "8px")
            }
        },
    ) {
        GalleryMono("on ${check.label}")
        val minimum = check.minimum
        when {
            minimum == null -> Badge(label = "$text ref", tone = BadgeTone.Neutral)
            ratio >= minimum -> Badge(label = "$text pass", tone = BadgeTone.Success)
            else -> Badge(label = "$text FAIL", tone = BadgeTone.Error)
        }
    }
}
